import { useCallback, useEffect, useRef, useState } from "react";
import { BrandMark } from "@/components/common/BrandLogo";
import { ModulesAnimation, useBrandAnimation } from "@/components/common/BrandAnimation";
import { suppressAuthReload } from "@/lib/authSync";
import { ArrowRight, Printer, Mic, Camera, Sun, Layers, Target, DoorOpen } from "lucide-react";
import { LiveSystem, PhoneFigure } from "./offer/LiveSystem";
import { CodaModules, InitiativeScene, RigBand } from "./offer/Visuals";
import "./offer.css";
import "./offer/system.css";

/**
 * Tilbod frå Kviteberg AS til Stord Innkvartering (etter møtet på Stord): ei felles retning for KI og digitalisering.
 * Runde 8 (redaksjonell komprimering, brukarens direktiv): ~25–30 % mindre tekst, same hovudhistorie –
 *   de har mykje på plass → fire veker for å forstå verksemda → dei viktigaste moglegheitene → A (forprosjekt) eller
 *   B (Kviteberg blir med vidare). Utgangspunktet + Retninga er éin kort blokk rett inn i dei fem spørsmåla;
 *   datagrunnlaget er halvert til ei påstand; spørsmåla under prototypen er borte; leveransen er fire korte punkt
 *   (A–G er éi linje); ti arbeidsøkter er vist per gruppe – ingen varigheiter eller tidspunkt (ikkje avtalt);
 *   to alternativ utan tabell, månadsfasar eller «Anbefalt».
 * Nynorsk. Ingenting er dikta opp utover brukarens tekst: ingen datoar, betalingsvilkår, bindingstid, kontaktinfo eller
 * signering. Ingen namn på møtedeltakarar utover mottakaren.
 *
 * Layout: éin sentrert lesekolonne (720 px) for prosa; breiare blokker (1120 px) bryt symmetrisk ut. Alt innhald er
 * alltid synleg; rørsle berre ved innlasting og i scenene (respekterer redusert rørsle).
 */

/* Nøkkeltala i heroen – utan pris (prisen høyrer heime i «Pris og alternativ») */
const LEDGER = [
  { label: "Forprosjekt", value: "4 veker", unit: "frå avtalt oppstart" },
  { label: "Arbeidsøkter", value: "10", unit: "med leiing, service, sal, prosjekt, leveranse og økonomi" },
  { label: "Prioriterte tiltak", value: "5–8", unit: "med effekt, kostnad, føresetnader og anbefalt neste steg" },
];

const LETTER = [
  "Takk for eit svært godt møte på Stord fredag.",
  "Det var tydeleg at Stord Innkvartering allereie har kome langt i arbeidet med digitalisering. De bruker moderne verktøy som Sana og Miro, fleire delar av organisasjonen testar KI og nye arbeidsmåtar, og det er både interesse og vilje internt til å jobbe smartare.",
  "Samtidig skjer mykje parallelt, medan teknologien utviklar seg svært raskt. Då er utfordringa ikkje å starte endå fleire prosjekt, men å finne ut kva som faktisk er verdt å satse på – og korleis initiativa kan trekkje i same retning.",
  "Det er her eg meiner Kviteberg kan bidra mest. Vi foreslår eit forprosjekt på fire veker, med ti fokuserte arbeidsøkter saman med leiing, service, sal, prosjekt, leveranse og økonomi. Etter fire veker skal de ha ei tydeleg retning for det vidare arbeidet og 5–8 konkrete tiltak de kan ta stilling til.",
  "Målet er ikkje ein omfattande strategirapport. Ein KI-strategi i 2026 kan ikkje vere ein statisk femårsplan – han må gjere organisasjonen betre i stand til å vurdere det som kjem. Ønskjer de at vi er med over tid, har vi også skissert eit alternativ der Kviteberg går inn som digitaliseringspartnar i tre månader.",
  "Eg ser fram til å jobbe vidare med dykk.",
];

const SCENE_BEATS = [
  { display: "Stord Innkvartering startar ikkje frå null.", sub: "Mykje er allereie sett i rørsle." },
  { display: "Mange gode initiativ – kvar for seg.", sub: "Initiativa skjer i stor grad lokalt." },
  { display: "Same initiativ – innanfor ei felles retning.", sub: "Neste steg er å sjå heilskapen." },
];

const FIVE_QS = [
  "Kvar kan Stord Innkvartering hente ut størst effekt no?",
  "Kva kan løysast betre med systema de allereie har?",
  "Kvar gir automatisering eller KI reell verdi?",
  "Kva bør standardiserast eller samordnast på tvers av organisasjonen?",
  "Og kvar kan det vere riktig å utvikle noko meir spesialisert?",
];

const FIELD = [
  { icon: Sun, title: "Dagen din", text: "Oppgåver, transport og relevant prosjektinformasjon samla rundt det som faktisk skal skje den dagen." },
  { icon: Camera, title: "Sjekklister og dokumentasjon", text: "Sjekklister, bilete og dokumentasjon blir registrerte direkte på riktig prosjekt medan arbeidet blir gjort." },
  { icon: Mic, title: "Registrere avvik med tale", text: "Ein medarbeidar forklarer kva som har skjedd med eigne ord. KI strukturerer det til eit forslag til avvik, som medarbeidaren kontrollerer og sender inn." },
];

/* Leveransen: fire punkt – kort og resultatnært */
const DELIVERABLES = [
  { title: "Felles retning", text: "Korleis Stord Innkvartering bør jobbe med KI og digitalisering vidare." },
  { title: "5–8 prioriterte tiltak", text: "Konkret vurderte etter effekt, kostnad og gjennomførbarheit." },
  { title: "Gevinst- og avgjerdsgrunnlag", text: "Kva tiltaka kan gi, og kva som må til for å realisere dei." },
  { title: "Vegkart", text: "Kva de bør gjere no, kva som kjem etterpå og kva det er verdt å planleggje for." },
];

/* Slik jobbar vi: fire veker på éi linje */
const WEEKS = [
  { n: "Veke 1", verb: "Forstå", text: "Arbeidsprosessane, systema de bruker og initiativa som allereie er i gang.", tag: "Oppstart" },
  { n: "Veke 2", verb: "Finne moglegheitene", text: "Betre bruk av det de har, automatisering, KI, integrasjonar eller ny programvare – ut frå behovet, ikkje teknologien." },
  { n: "Veke 3", verb: "Prioritere", text: "5–8 tiltak, vurderte etter effekt, kostnad, risiko og føresetnader.", tag: "Prioriteringsmøte" },
  { n: "Veke 4", verb: "Samle retninga", text: "Kort strategi og konkret vegkart. Vi utfordrar føresetnadene saman før endeleg levering.", tag: "Sluttgjennomgang" },
];

/* Ti arbeidsøkter per gruppe – ingen varigheiter eller tidspunkt (ikkje avtalt, difor ikkje påstått) */
const SESSION_GROUPS = [
  { who: "Leiing", n: 4, what: "Oppstart · strategiske mål · prioritering · sluttgjennomgang" },
  { who: "Service", n: 2 },
  { who: "Sal", n: 2 },
  { who: "Prosjekt og leveranse", n: 1 },
  { who: "Økonomi og system", n: 1 },
];
const SESSION_TOTAL = SESSION_GROUPS.reduce((s, g) => s + g.n, 0);

/* To alternativ – same arbeidsmåte, ulik varigheit og rolle. Prisstige (brukarens val): 90 000 → 180 000. */
const ALTS = [
  {
    id: "a",
    tag: "Alternativ A",
    title: "Forprosjekt",
    meta: ["4 veker", "90 000 kr", "eks. mva. · fastpris"],
    what: "Vi finn ut kva de bør gjere.",
    text: "Eit intensivt forprosjekt som gir dykk eit konkret grunnlag for kva de bør prioritere i det vidare arbeidet.",
  },
  {
    id: "b",
    tag: "Alternativ B · Løpande samarbeid",
    title: "Digitaliseringspartnar",
    meta: ["3 månader", "60 000 kr/mnd", "eks. mva. · 180 000 kr totalt"],
    what: "Vi finn ut kva de bør gjere – og hjelper dykk i gang med det.",
    text: "Vi gjer forprosjektet – og blir med vidare for å teste, konkretisere og drive dei prioriterte tiltaka framover.",
  },
];

const PREMISES = [
  { icon: Layers, b: "Vi byggjer vidare på det som fungerer", s: "Dagens ERP, Sana, Miro og andre etablerte verktøy er delar av løysinga – ikkje problem som må erstattast. Eit stort systembyte er ikkje premisset for arbeidet." },
  { icon: Target, b: "Behovet kjem før teknologien", s: "Vi startar ikkje med «Kvar kan vi bruke KI?», men med «Kva bør bli betre?». Så vel vi verkemiddel – og nokre gonger er den beste løysinga å ikkje byggje noko nytt." },
  { icon: DoorOpen, b: "De står fritt etter leveransen", s: "Kviteberg kan bidra vidare med rådgjeving, integrasjonar og programvare dersom det er riktig for Stord Innkvartering. Men det er ikkje ein føresetnad for oppdraget." },
];

const NEXT_STEPS = [
  { t: "Gje beskjed om de vil gå vidare", s: "– og med kva alternativ." },
  { t: "Vi avtalar oppstart", s: "og tidspunkt for arbeidsøktene." },
  { t: "De får ei kort liste over underlag", s: "– så bruker vi øktene på arbeidsprosessane og dei viktigaste spørsmåla, ikkje på å samle grunninformasjon." },
];

/* ---------- små byggesteinar ---------- */
const SectionHead = ({ eyebrow, title, text, as: Tag = "h2", testId, className = "" }) => (
  <div className={`offer-head ${className}`} data-testid={testId}>
    <span className="offer-eyebrow">{eyebrow}</span>
    <Tag className={Tag === "h3" ? "offer-h2 offer-h2-sm" : "offer-h2"}>{title}</Tag>
    {(Array.isArray(text) ? text : text ? [text] : []).map((t) => <p key={t} className="offer-lede">{t}</p>)}
  </div>
);

const Questions = ({ items, testId, onInk, xl }) => (
  <ul className={`offer-qs ${onInk ? "on-ink" : ""} ${xl ? "offer-qs-xl" : ""}`} data-testid={testId}>
    {items.map((q, i) => <li key={q}><span className="offer-num offer-tabular">0{i + 1}</span><span>{q}</span></li>)}
  </ul>
);

const Signature = ({ testId }) => (
  <div className="offer-sign" data-testid={testId}>
    <img src="/brand/martin-kviteberg-portrett.jpg" alt="Martin Kviteberg" className="offer-sign-photo" draggable={false} />
    <div><div className="offer-sign-name">Martin Kviteberg</div><div className="offer-sign-role">Kviteberg AS</div></div>
  </div>
);

/* topplinja ligg over heroen: transparent på navy til heroen er rulla forbi (rAF-throttla scroll – deterministisk) */
const useOverHero = (heroRef) => {
  const [over, setOver] = useState(true);
  useEffect(() => {
    let raf = 0;
    const read = () => {
      raf = 0;
      const el = heroRef.current;
      if (!el) return;
      setOver(el.getBoundingClientRect().bottom > 72);
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [heroRef]);
  return over;
};

const prefersReduce = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function KvitebergOfferPage() {
  const anim = useBrandAnimation();
  const heroRef = useRef(null);
  const overHero = useOverHero(heroRef);
  const [codaDone, setCodaDone] = useState(false);
  const markCodaDone = useCallback(() => setCodaDone(true), []);
  useEffect(() => {
    const prev = document.title;
    document.title = "Tilbod: Ei felles retning for KI og digitalisering · Kviteberg AS for Stord Innkvartering";
    /* rammene loggar inn i demoen – sida sjølv skal ikkje lastast på nytt av det */
    suppressAuthReload(true);
    /* mjuk rulling til ankera (ikkje ved redusert rørsle) */
    const root = document.documentElement, prevScroll = root.style.scrollBehavior;
    if (!prefersReduce()) root.style.scrollBehavior = "smooth";
    return () => { document.title = prev; suppressAuthReload(false); root.style.scrollBehavior = prevScroll; };
  }, []);
  const print = () => window.print();

  return (
    <div className="offer" data-testid="offer-page">
      <a href="#brev" className="offer-skip">Hopp til innhald</a>

      <header className="offer-top" data-testid="offer-top" data-over-hero={overHero ? "1" : "0"}>
        <div className="offer-top-row">
          <div className="offer-top-brands">
            <img src="/brand/kviteberg-logo.svg" alt="Kviteberg AS" draggable={false} data-testid="offer-kviteberg-mark" />
            <span className="offer-top-for">for</span>
            <span className="offer-brand-stord" data-testid="offer-stord-mark"><BrandMark className="h-6 w-6" color={overHero ? "#FFFFFF" : "#0B1220"} accent={overHero ? "#8DB1F8" : "#2670F2"} /><span className="offer-brand-stord-name">Stord Innkvartering</span></span>
          </div>
          <div className="offer-top-actions">
            <button type="button" className="offer-btn offer-btn-sm" onClick={print} data-testid="offer-print-button"><Printer className="h-4 w-4" /> <span className="hidden sm:inline">Skriv ut / PDF</span><span className="sm:hidden">PDF</span></button>
          </div>
        </div>
      </header>

      <main>
        {/* ---- Hero: fyller heile viewporten. Navy flate med modulanimasjonen, lite tekst, nøkkeltala som stripe i botnen. ---- */}
        <section id="oversikt" ref={heroRef} className="offer-hero" data-testid="offer-hero">
          <div className="offer-hero-grid module-grid-bg" aria-hidden="true" />
          <div className="offer-hero-body">
            <div className="offer-hero-split">
              <div className="offer-hero-copy">
                <p className="offer-kicker offer-rise" data-testid="offer-hero-kicker">Forprosjekt · Tilbod til Stord Innkvartering</p>
                <h1 className="offer-h1" data-testid="offer-hero-title">
                  <span className="offer-mask"><span className="offer-line" style={{ "--d": "0.08s" }}>Ei felles retning for</span></span>
                  <span className="offer-mask"><span className="offer-line" style={{ "--d": "0.2s" }}>KI og digitalisering</span></span>
                </h1>
                <p className="offer-hero-sub offer-rise" style={{ "--d": "0.42s" }} data-testid="offer-hero-sub">De har allereie kome godt i gang. No handlar det om å samle initiativa, finne dei viktigaste moglegheitene og bli einige om kva de faktisk skal prioritere vidare.</p>
                <div className="offer-hero-links offer-rise" style={{ "--d": "0.54s" }}>
                  <a href="#moglegheitsbiletet" className="offer-textlink" data-testid="offer-link-system">Sjå moglegheitsbiletet <ArrowRight className="h-4 w-4" /></a>
                  <a href="#pris" className="offer-textlink offer-textlink-muted" data-testid="offer-link-price">Pris og alternativ <ArrowRight className="h-4 w-4" /></a>
                </div>
              </div>
              <div className="offer-hero-art offer-rise" style={{ "--d": "0.3s" }} data-testid="offer-hero-art">
                <div className="offer-hero-glow" aria-hidden="true" />
                <ModulesAnimation data={anim} className="offer-hero-lottie" testId="offer-hero-animation" />
              </div>
            </div>
          </div>
          <dl className="offer-hero-facts offer-rise" style={{ "--d": "0.7s" }} data-testid="offer-ledger">
            {LEDGER.map((f, i) => (
              <div key={f.label} className="offer-fact" data-testid={`offer-fact-${i}`}>
                <dt className="offer-fact-label">{f.label}</dt>
                <dd className="offer-fact-value offer-tabular">{f.value}</dd>
                <dd className="offer-fact-unit">{f.unit}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* ---- Brev (seks korte avsnitt) ---- */}
        <section id="brev" className="offer-section" data-testid="offer-letter">
          <div className="offer-wrap offer-letter">
            <h2 className="offer-greeting">Hei Endre,</h2>
            {LETTER.map((p) => <p key={p} className="offer-p offer-p-lg">{p}</p>)}
            <Signature testId="offer-signature" />
          </div>
        </section>

        {/* ---- Utgangspunktet (kort) → scena → rett inn i dei fem spørsmåla ---- */}
        <section id="utgangspunktet" className="offer-section offer-band offer-start" data-testid="offer-starting-point">
          <div className="offer-wrap">
            <div className="offer-head offer-head-xl" data-testid="offer-starting-point-head">
              <span className="offer-eyebrow">Utgangspunktet</span>
              <h2 className="offer-h2 offer-h2-xl">De har allereie mykje på plass</h2>
            </div>
            <div className="offer-prose offer-start-prose" data-testid="offer-starting-point-text">
              <p className="offer-p offer-p-lg">Stord Innkvartering bruker moderne system, og fleire delar av organisasjonen testar nye arbeidsmåtar og KI. Neste steg er ikkje nødvendigvis fleire initiativ, men <strong className="offer-strong">ei tydelegare retning</strong> for korleis system, data og KI skal henge saman på tvers av verksemda.</p>
            </div>
          </div>
          <InitiativeScene beats={SCENE_BEATS} />
          <div className="offer-wrap" id="sporsmala">
            <div className="offer-head offer-head-sub" data-testid="offer-questions">
              <span className="offer-eyebrow">Kva vi skal finne ut</span>
              <h3 className="offer-h2 offer-h2-sm">Fem sentrale spørsmål</h3>
              <p className="offer-lede">Vi bruker fire veker på å svare på dei – med særleg vekt på service og sal, der vi allereie såg fleire moglegheiter i møtet.</p>
            </div>
            <Questions items={FIVE_QS} testId="offer-five-questions" xl />
            <div className="offer-prose">
              <p className="offer-pull offer-pull-bar" data-testid="offer-early-value">Vi ventar ikkje til veke fire med å skape verdi. Tiltak som bør testast eller konkretiserast undervegs, tek vi tak i fortløpande.</p>
              <p className="offer-p offer-p-muted" data-testid="offer-early-value-examples">Det kan vere alt frå ei enkel automatisering til ei mobil feltløysing, eit system for tilbodsgenerering eller ein integrasjon mellom eksisterande system.</p>
            </div>
          </div>
        </section>

        {/* ---- Riggen som blir bygd (3D) + datagrunnlaget som éi påstand ---- */}
        <RigBand eyebrow="Stord Innkvartering" title="Kvar leveranse skaper data." text="Kundar og tilbod, rigg og modular, prosjekt og framdrift, transport, innkvartering, dokumentasjon og økonomi." />
        <section id="data" className="offer-section offer-ink offer-datapoint" data-testid="offer-data">
          <div className="offer-wrap">
            <span className="offer-eyebrow">Datagrunnlaget</span>
            <p className="offer-hub-foot" data-testid="offer-hub-foot">KI blir først verkeleg nyttig når han kan arbeide med informasjonen de <em>allereie har</em>.</p>
            <div className="offer-prose" data-testid="offer-data-text">
              <p className="offer-p offer-p-lg">Målet er ikkje å samle alt i eitt nytt system, men å finne ut korleis relevante data frå ERP, dokument, e-post, felt og andre system kan brukast betre på tvers. Korleis dette bør løysast hos Stord Innkvartering, er noko av det vi skal finne ut.</p>
            </div>
          </div>
        </section>

        {/* ---- Moglegheitsbiletet: sjølve prototypen, nesten kant til kant – klikk deg rundt. Så feltdømet, kort. ---- */}
        <section id="moglegheitsbiletet" className="offer-section offer-live-sec" data-testid="offer-system">
          <div className="offer-wrap">
            <SectionHead eyebrow="Moglegheitsbiletet" title="Kva blir mogleg når arbeidsflytane heng betre saman?" text={["For å gjere diskusjonen konkret har vi laga ein enkel prototype basert på forståinga vår så langt – klikk deg rundt.", "Han er ikkje eit forslag om å erstatte dagens ERP, og ikkje ein del av leveransen. Han viser kva som kan bli mogleg når data, arbeidsflytar og KI heng betre saman."]} />
          </div>
          <LiveSystem />

          <div className="offer-field" data-testid="offer-field">
            <div className="offer-wrap">
              <SectionHead as="h3" eyebrow="Eit døme frå felt" title="Kva kan bli enklare for dei som står i arbeidet?" text="Nokre enkle mobile arbeidsflytar – ikkje ein ferdig spesifikasjon, men døme på korleis små forbetringar fjernar mange manuelle steg når dei blir gjentekne kvar dag." />
            </div>
            <div className="offer-wide offer-field-grid">
              <PhoneFigure width={330} testId="offer-field-phone" className="offer-field-phone" />
              <ol className="offer-field-points">
                {FIELD.map((f, i) => (
                  <li key={f.title} className="offer-card" data-testid={`offer-field-point-${i}`}>
                    <span className="offer-card-icon"><f.icon className="h-[18px] w-[18px]" /></span>
                    <span className="offer-num offer-tabular">0{i + 1}</span>
                    <b>{f.title}</b>
                    <span>{f.text}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* ---- Dette leverer vi: fire punkt + éi linje om formatet ---- */}
        <section id="leveranse" className="offer-section offer-band" data-testid="offer-deliverables">
          <div className="offer-wrap">
            <SectionHead eyebrow="Leveransen" title="Dette leverer vi" text="Etter fire veker har Stord Innkvartering eit konkret grunnlag for å prioritere det vidare arbeidet. Fire delar:" />
          </div>
          <ol className="offer-wide offer-deliv" data-testid="offer-deliv-grid">
            {DELIVERABLES.map((d, i) => (
              <li key={d.title} className="offer-deliv-item" data-testid={`offer-deliverable-${i}`}>
                <span className="offer-deliv-n offer-tabular">0{i + 1}</span>
                <h3 className="offer-h3 offer-deliv-title">{d.title}</h3>
                <p className="offer-deliv-text">{d.text}</p>
              </li>
            ))}
          </ol>
          <div className="offer-wide">
            <p className="offer-deliv-note" data-testid="offer-method">Kvart tiltak blir beskrive likt – dagens situasjon, forslag, forventa effekt, kostnadsnivå, føresetnader, risiko og anbefalt neste steg – slik at dei er lette å samanlikne og ta stilling til.</p>
          </div>
        </section>

        {/* ---- Slik jobbar vi: fire veker på éi linje + ti arbeidsøkter per gruppe ---- */}
        <section id="framdrift" className="offer-section" data-testid="offer-progress">
          <div className="offer-wrap">
            <SectionHead eyebrow="Slik jobbar vi" title="Fire veker. Ti arbeidsøkter." text="Vi startar med arbeidsprosessane – teknologien kjem etterpå." />
          </div>
          <ol className="offer-wide offer-weeks" data-testid="offer-weeks">
            {WEEKS.map((w, i) => (
              <li key={w.n} className="offer-week" data-testid={`offer-week-${i + 1}`}>
                <div className="offer-week-k"><span className="offer-week-n">{w.n}</span><span className="offer-week-verb">{w.verb}</span></div>
                <div className="offer-week-body">
                  <p>{w.text}</p>
                  {w.tag && <span className="offer-week-tag">{w.tag}</span>}
                </div>
              </li>
            ))}
          </ol>

          <div className="offer-wide offer-sessions-wrap" id="moter">
            <div className="offer-sessions-intro">
              <h3 className="offer-h3 offer-h3-lg">Ti arbeidsøkter</h3>
              <p className="offer-small">Korte, fokuserte økter – ikkje ti store workshopar. Tidspunkta avtalar vi ved oppstart.</p>
            </div>
            <div className="offer-tally" data-testid="offer-sessions" role="table" aria-label="Arbeidsøkter per gruppe">
              {SESSION_GROUPS.map((g, i) => (
                <div key={g.who} className="offer-tally-row" role="row" data-testid={`offer-session-group-${i}`}>
                  <span className="offer-tally-who" role="cell">{g.who}</span>
                  <span className="offer-tally-dots" role="cell" aria-hidden="true">
                    {Array.from({ length: SESSION_TOTAL }, (_, k) => <i key={k} className={k < g.n ? "on" : ""} />)}
                  </span>
                  <span className="offer-tally-n offer-tabular" role="cell">{g.n} {g.n === 1 ? "arbeidsøkt" : "arbeidsøkter"}</span>
                  {g.what && <span className="offer-tally-what" role="cell">{g.what}</span>}
                </div>
              ))}
              <div className="offer-tally-row offer-tally-sum" role="row" data-testid="offer-sessions-total">
                <span className="offer-tally-who" role="cell">Til saman</span>
                <span className="offer-tally-dots" role="cell" aria-hidden="true">{Array.from({ length: SESSION_TOTAL }, (_, k) => <i key={k} className="on" />)}</span>
                <span className="offer-tally-n offer-tabular" role="cell">= {SESSION_TOTAL} fokuserte arbeidsøkter over fire veker</span>
              </div>
            </div>
          </div>
        </section>

        {/* ---- Pris og alternativ (blekk): to kort, éi avgrensing ---- */}
        <section id="pris" className="offer-section offer-ink" data-testid="offer-pricing">
          <div className="offer-wrap">
            <SectionHead eyebrow="Pris og alternativ" title="To måtar å gjere det på" text="Same arbeidsmåte. Skilnaden er kor lenge vi er med – og kor langt vi følgjer tiltaka." />
          </div>
          <div className="offer-wide offer-alts" data-testid="offer-alternatives">
            {ALTS.map((a) => (
              <article key={a.id} className={`offer-alt offer-alt-${a.id}`} data-testid={`offer-alt-${a.id}`}>
                <span className="offer-alt-tag">{a.tag}</span>
                <h3 className="offer-alt-title">{a.title}</h3>
                <p className="offer-alt-meta"><span className="offer-alt-value offer-tabular" data-testid={a.id === "a" ? "offer-price" : "offer-price-b"}>{a.meta[1]}</span><span className="offer-alt-dur offer-tabular">{a.meta[0]}</span><span className="offer-alt-unit">{a.meta[2]}</span></p>
                <p className="offer-alt-what" data-testid={`offer-alt-${a.id}-what`}>{a.what}</p>
                <p className="offer-alt-desc">{a.text}</p>
              </article>
            ))}
          </div>
          <div className="offer-wide">
            <p className="offer-alt-note" data-testid="offer-scope-excluded">Programvareutvikling og større tekniske leveransar blir avtalt separat.</p>
          </div>
        </section>

        {/* ---- Tre premiss ---- */}
        <section id="premiss" className="offer-section offer-band" data-testid="offer-premises">
          <div className="offer-wrap">
            <SectionHead eyebrow="Premiss" title="Tre ting vi legg til grunn" />
          </div>
          <ol className="offer-wide offer-frames" data-testid="offer-frames">
            {PREMISES.map((f, i) => (
              <li key={f.b} className="offer-frame" data-testid={`offer-frame-${i}`}>
                <span className="offer-frame-icon"><f.icon className="h-5 w-5" /></span>
                <span className="offer-num offer-tabular">0{i + 1}</span>
                <b>{f.b}</b>
                <p>{f.s}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ---- Neste steg ---- */}
        <section id="neste" className="offer-section" data-testid="offer-next-steps">
          <div className="offer-wide offer-next">
            <div className="offer-next-copy">
              <span className="offer-eyebrow">Neste steg</span>
              <h2 className="offer-h2 offer-h2-sm">Dersom de ønskjer å gå vidare</h2>
              <ol className="offer-steps" data-testid="offer-steps">
                {NEXT_STEPS.map((s, i) => <li key={s.t}><span className="offer-step-n offer-tabular">{i + 1}</span><span><b>{s.t}</b> {s.s}</span></li>)}
              </ol>
              <Signature testId="offer-signature-bottom" />
            </div>
            <div className="offer-next-actions"><button type="button" className="offer-btn" onClick={print} data-testid="offer-print-button-bottom"><Printer className="h-4 w-4" /> Skriv ut / lagre som PDF</button></div>
          </div>
        </section>

        {/* ---- Avslutning: eit roleg sluttbilete som speglar heroen – navy, modulane byggjer seg opp éin gong og blir ståande,
             så tittelen kjem att som eit ekko. Kolofonen ligg i botnen. ---- */}
        <section className="offer-coda" data-testid="offer-coda" data-state={codaDone ? "done" : "pre"} aria-labelledby="offer-coda-title">
          <div className="offer-coda-grid module-grid-bg" aria-hidden="true" />
          <div className="offer-coda-body">
            <CodaModules data={anim} onDone={markCodaDone} />
            <p className="offer-coda-kicker" data-testid="offer-coda-kicker">Kviteberg AS for Stord Innkvartering</p>
            <p id="offer-coda-title" className="offer-coda-title" data-testid="offer-coda-title">Ei felles retning for KI og digitalisering.</p>
          </div>
          <footer className="offer-coda-foot" data-testid="offer-footer">
            <span>Kviteberg AS · Tilbod til Stord Innkvartering</span>
            <span>Tilbod utarbeidd etter møte på Stord</span>
          </footer>
        </section>
      </main>
    </div>
  );
}
